// src/ui/screens/chats/ChatScreen.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { UserAvatar } from '../../../components/UserAvatar';
import { MessageBubble } from './widgets/MessageBubble';
import { MessageInput } from './widgets/MessageInput';
import { MessageSelectionBar } from './widgets/MessageSelectionBar';
import { PinnedMessagesBanner, PinnedMessageInfo } from './widgets/PinnedMessagesBanner';
import { chatActionsService } from '../../../services/chat/chatActionsService';
import { messageActionsService } from '../../../services/chat/messageActionsService';
import { chatLocalDb } from '../../../services/chat/chatLocalDb';
import { mediaUploadService } from '../../../services/chat/mediaUploadService';
import { firebaseMessageService } from '../../../services/chat/messageService';
import { userProfileService } from '../../../services/user/userProfileService';
import { db } from '../../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

// Must be imported (not referenced as a bare "src/..." string) so the bundler
// resolves and fingerprints the asset — mirrors Flutter's Image.asset(...).
import chatWallpaper from '../../../assets/images/chat_wallpaper.jpg';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
  blue: '#4DA3FF',
  red: '#FF1744',
};

// Cap for the standalone desktop chat page (opened via a direct route from
// another screen, e.g. "Message seller" on a listing). Keeps the reading
// column a sane width instead of stretching the bubbles across an
// ultra-wide browser. Not applied when `embedded` — the split-pane usage
// inside ConversationsScreen already has its own width via the layout.
const STANDALONE_MAX_WIDTH = 780;

// ── ChatScreen ──────────────────────────────────────────────────────────────
//
// Two ways this screen is used:
//   1. Standalone (default) — mounted on its own route (/chat/:conversationId),
//      reading its data from useParams/useLocation. This is the "full chat
//      screen" opened when navigating in from another page (a listing's
//      "Message seller" button, a profile, etc).
//   2. Embedded — rendered inline by ConversationsScreen's desktop split
//      view when a conversation tile is tapped. Pass `embedded` plus the
//      conversation's data directly as props (no route involved), and an
//      `onClose` callback so the "Close chat" menu item can hand control
//      back to the parent instead of trying to navigate(-1).

export const ChatScreen = (props = {}) => {
  const {
    embedded = false,
    conversationId: propConversationId,
    receiverId: propReceiverId,
    title: propTitle,
    avatarUrl: propAvatarUrl,
    onClose,
  } = props;

  const navigate = useNavigate();
  const { conversationId: paramConversationId } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  // Get params from props (embedded), location state, or the URL param —
  // in that priority order.
  const conversationId = propConversationId || paramConversationId || location.state?.conversationId || '';
  const receiverId = propReceiverId || location.state?.receiverId || '';
  const title = propTitle || location.state?.title || 'Chat';
  const avatarUrl = propAvatarUrl || location.state?.avatarUrl || null;

  const [messages, setMessages] = useState([]);
  const [receiverProfile, setReceiverProfile] = useState(null);
  const [presence, setPresence] = useState({ isOnline: false, lastSeenAt: null });
  const [isBlocked, setIsBlocked] = useState(false);
  const [amIBlocked, setAmIBlocked] = useState(false);
  const [blockTimestamp, setBlockTimestamp] = useState(null);
  const [blockStateLoaded, setBlockStateLoaded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
  const [replyingTo, setReplyingTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [pinnedMessageIds, setPinnedMessageIds] = useState([]);
  const [pinnedMessageInfos, setPinnedMessageInfos] = useState([]);
  const [liveStatuses, setLiveStatuses] = useState({});
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const messageKeys = useRef(new Map());
  const currentUserId = user?.uid || '';

  // ── Format time ────────────────────────────────────────────────────────────

  const formatTime = (date) => {
    if (!date) return '';
    const dt = date instanceof Date ? date : new Date(date);
    let hours = dt.getHours();
    const minutes = dt.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDividerDate = (date) => {
    if (!date) return '';
    const dt = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const isToday = dt.getDate() === now.getDate() &&
      dt.getMonth() === now.getMonth() &&
      dt.getFullYear() === now.getFullYear();
    if (isToday) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dt.getDate() === yesterday.getDate() &&
        dt.getMonth() === yesterday.getMonth() &&
        dt.getFullYear() === yesterday.getFullYear()) {
      return 'Yesterday';
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (dt.getFullYear() === now.getFullYear()) {
      return `${days[dt.getDay()]}, ${dt.getDate()} ${months[dt.getMonth()]}`;
    }
    return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
  };

  // ── Presence text ─────────────────────────────────────────────────────────

  const getPresenceText = () => {
    if (presence.isOnline) return 'Online';
    const lastSeen = presence.lastSeenAt;
    if (!lastSeen) return 'Offline';
    const now = new Date();
    const diff = now - new Date(lastSeen);
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return 'Last seen just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Last seen ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      const dt = new Date(lastSeen);
      let h = dt.getHours() % 12 || 12;
      const m = dt.getMinutes().toString().padStart(2, '0');
      const ampm = dt.getHours() >= 12 ? 'PM' : 'AM';
      return `Last seen today at ${h}:${m} ${ampm}`;
    }
    const days = Math.floor(hours / 24);
    if (days === 1) {
      const dt = new Date(lastSeen);
      let h = dt.getHours() % 12 || 12;
      const m = dt.getMinutes().toString().padStart(2, '0');
      const ampm = dt.getHours() >= 12 ? 'PM' : 'AM';
      return `Last seen yesterday at ${h}:${m} ${ampm}`;
    }
    const dt = new Date(lastSeen);
    return `Last seen ${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  };

  // ── Load receiver profile ─────────────────────────────────────────────────

  useEffect(() => {
    const loadProfile = async () => {
      if (!receiverId) return;
      try {
        const profile = await userProfileService.getProfileByUid(receiverId);
        if (profile) setReceiverProfile(profile);
      } catch (error) {
        console.error('Load receiver profile error:', error);
      }
    };
    loadProfile();
  }, [receiverId]);

  // ── Load block state ──────────────────────────────────────────────────────

  useEffect(() => {
    const loadBlockState = async () => {
      if (!receiverId || !currentUserId) {
        setBlockStateLoaded(true);
        return;
      }
      try {
        const blockDoc = await getDoc(doc(db, 'blockedUsers', currentUserId, 'blocked', receiverId));
        let iBlocked = false;
        let blockTs = null;
        if (blockDoc.exists()) {
          iBlocked = true;
          const data = blockDoc.data();
          if (data?.blockedAt?.toDate) {
            blockTs = data.blockedAt.toDate();
          }
        }
        const reverseDoc = await getDoc(doc(db, 'blockedUsers', receiverId, 'blocked', currentUserId));
        setIsBlocked(iBlocked);
        setBlockTimestamp(blockTs);
        setAmIBlocked(reverseDoc.exists());
      } catch (error) {
        console.error('Load block state error:', error);
      } finally {
        setBlockStateLoaded(true);
      }
    };
    loadBlockState();
  }, [receiverId, currentUserId]);

  // ── Presence lifecycle (mirrors Flutter's initState/dispose + ────────────
  // ── WidgetsBindingObserver.didChangeAppLifecycleState) ────────────────────

  useEffect(() => {
    firebaseMessageService.startPresence();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        firebaseMessageService.startPresence();
      } else {
        firebaseMessageService.goOffline();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      firebaseMessageService.goOffline();
    };
  }, []);

  // ── Subscribe to receiver's presence ─────────────────────────────────────

  useEffect(() => {
    if (!receiverId) return;

    const unsubscribe = firebaseMessageService.presenceStream(receiverId, (p) => {
      setPresence({ isOnline: p.isOnline, lastSeenAt: p.lastSeenAt });
    });

    return () => unsubscribe();
  }, [receiverId]);

  // ── Subscribe to pinned messages ─────────────────────────────────────────

  useEffect(() => {
    if (!conversationId) return;

    const unsubscribe = onSnapshot(doc(db, 'chats', conversationId), async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const ids = data.pinnedMessageIds || [];
        setPinnedMessageIds(ids);
        // Fetch pinned message details
        const infos = [];
        for (const id of ids) {
          const msgSnap = await getDoc(doc(db, 'chats', conversationId, 'messages', id));
          if (msgSnap.exists()) {
            const msgData = msgSnap.data();
            const type = msgData.msgType || 'text';
            let preview = msgData.msg || '';
            if (type === 'image') preview = '📷 Photo';
            else if (type === 'audio') preview = '🎤 Voice message';
            else if (type === 'video') preview = '🎥 Video';
            else if (preview.length > 60) preview = preview.substring(0, 60) + '…';
            infos.push(new PinnedMessageInfo({
              id: id,
              preview: preview,
              type: type,
              attachmentUrl: msgData.attachmentUrl || null,
            }));
          }
        }
        setPinnedMessageInfos(infos);
      }
    }, (error) => {
      console.error('Pinned messages subscription error:', error);
    });

    return () => unsubscribe();
  }, [conversationId]);

  // ── Subscribe to messages ────────────────────────────────────────────────

  useEffect(() => {
    if (!conversationId) return;

    // Load cached messages
    const loadCached = async () => {
      try {
        const cached = await chatLocalDb.loadMessages(conversationId);
        if (cached && cached.length > 0) {
          setMessages(cached);
          setTimeout(() => scrollToBottom(), 100);
        }
      } catch (error) {
        console.error('Load cached messages error:', error);
      }
    };
    loadCached();

    // Mirrors Flutter's _initFirebase order: mark delivered, then subscribe,
    // then mark the conversation read (locally + remotely).
    firebaseMessageService.markIncomingDelivered(conversationId);

    // Subscribe to real-time messages via the shared message service, so the
    // status/messageType conversion logic lives in exactly one place.
    const unsubscribeMessages = firebaseMessageService.messagesStream(conversationId, (incoming) => {
      // Filter messages deleted for current user
      const visible = incoming.filter((m) => !(m.deletedFor?.includes(currentUserId) || false));

      chatLocalDb.saveMessages(visible);
      setMessages(visible);
      setTimeout(() => scrollToBottom(), 100);
    });

    // Subscribe to my own messages' status updates (sent/delivered/read)
    const unsubscribeStatus = firebaseMessageService.myMessageStatusStream(conversationId, (statusMap) => {
      setLiveStatuses(statusMap);
    });

    chatActionsService.markAsRead(conversationId);
    chatLocalDb.markConversationRead(conversationId);

    return () => {
      unsubscribeMessages();
      unsubscribeStatus();
    };
  }, [conversationId, currentUserId]);

  // ── Scroll to bottom ──────────────────────────────────────────────────────

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  // ── Scroll to message ─────────────────────────────────────────────────────

  const scrollToMessage = (messageId) => {
    const key = messageKeys.current.get(messageId);
    if (key?.current) {
      key.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(messageId);
      setTimeout(() => setHighlightedMessageId(null), 1200);
    }
  };

  // ── Unpin message ─────────────────────────────────────────────────────────

  const unpinMessage = async (messageId) => {
    try {
      await messageActionsService.unpinMessage(conversationId, messageId);
    } catch (error) {
      console.error('Unpin error:', error);
    }
  };

  // ── Unblock user ──────────────────────────────────────────────────────────

  const unblockUser = async () => {
    if (window.confirm(`Unblock ${title}? They will be able to send you messages again.`)) {
      try {
        await chatActionsService.unblockUser(receiverId);
        setIsBlocked(false);
        setBlockTimestamp(null);
      } catch (error) {
        console.error('Unblock error:', error);
      }
    }
  };

  // ── Send message ──────────────────────────────────────────────────────────

  const handleSend = async () => {
    // Handle media messages
    if (pendingImages.length > 0) {
      await handleSendMedia();
      return;
    }

    const text = inputRef.current?.value || '';
    if (!text.trim() || isSending) return;

    if (amIBlocked) {
      alert("You can't send messages to this user");
      return;
    }

    setIsSending(true);
    const tempId = `temp_${Date.now()}`;
    const tempMsg = {
      id: tempId,
      conversationId,
      senderId: currentUserId,
      receiverId,
      content: text.trim(),
      timestamp: new Date(),
      status: 'sending',
      messageType: 'text',
      replyToMessageId: replyingTo?.id || null,
      replyToContent: replyingTo?.content || null,
      replyToSenderId: replyingTo?.senderId || null,
      replyToType: replyingTo?.messageType || null,
    };

    setMessages(prev => [...prev, tempMsg]);
    if (inputRef.current) inputRef.current.value = '';
    setReplyingTo(null);
    setTimeout(() => scrollToBottom(), 50);

    try {
      // Send via the same service the Flutter screen uses (FirebaseMessageService)
      await firebaseMessageService.sendMessage({
        conversationId,
        receiverId,
        content: text.trim(),
        replyMetadata: replyingTo ? {
          replyToId: replyingTo.id,
          replyToContent: replyingTo.content,
          replyToSenderId: replyingTo.senderId,
          replyToType: replyingTo.messageType,
          replyToAttachmentUrl: replyingTo.attachmentUrl,
        } : null,
      });
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } catch (error) {
      console.error('Send message error:', error);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'failed' } : m
      ));
    } finally {
      setIsSending(false);
    }
  };

  // ── Send media ─────────────────────────────────────────────────────────────

  const handleSendMedia = async () => {
    if (pendingImages.length === 0) return;

    const caption = inputRef.current?.value || '';
    if (inputRef.current) inputRef.current.value = '';
    const replyMeta = replyingTo ? {
      replyToId: replyingTo.id,
      replyToContent: replyingTo.content,
      replyToSenderId: replyingTo.senderId,
      replyToType: replyingTo.messageType,
      replyToAttachmentUrl: replyingTo.attachmentUrl,
    } : null;
    setReplyingTo(null);

    for (const image of pendingImages) {
      const tempId = `temp_${Date.now()}_${image.name || 'image'}`;
      const tempMsg = {
        id: tempId,
        conversationId,
        senderId: currentUserId,
        receiverId,
        content: caption,
        timestamp: new Date(),
        status: 'sending',
        messageType: 'image',
        localFilePath: image.path || null,
        replyToMessageId: replyMeta?.replyToId || null,
        replyToContent: replyMeta?.replyToContent || null,
        replyToSenderId: replyMeta?.replyToSenderId || null,
        replyToType: replyMeta?.replyToType || null,
      };
      setMessages(prev => [...prev, tempMsg]);
      setTimeout(() => scrollToBottom(), 50);

      try {
        const imageUrl = await mediaUploadService.uploadImage(image);
        await firebaseMessageService.sendMessage({
          conversationId,
          receiverId,
          content: caption,
          messageType: 'image',
          attachmentUrl: imageUrl,
          attachmentType: 'image/jpeg',
          replyMetadata: replyMeta,
        });
        setMessages(prev => prev.filter(m => m.id !== tempId));
      } catch (error) {
        console.error('Send image error:', error);
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...m, status: 'failed' } : m
        ));
      }
    }
    setPendingImages([]);
  };

  // ── Send voice note ────────────────────────────────────────────────────────

  const handleVoiceRecorded = async (audioFile, duration) => {
    if (amIBlocked) {
      alert("You can't send messages to this user");
      return;
    }

    const tempId = `temp_${Date.now()}_voice`;
    const tempMsg = {
      id: tempId,
      conversationId,
      senderId: currentUserId,
      receiverId,
      content: 'Voice message',
      timestamp: new Date(),
      status: 'sending',
      messageType: 'audio',
      localFilePath: audioFile.path || null,
      audioDuration: duration,
    };
    setMessages(prev => [...prev, tempMsg]);
    setTimeout(() => scrollToBottom(), 50);

    try {
      const audioUrl = await mediaUploadService.uploadAudio(audioFile, duration);
      await firebaseMessageService.sendMessage({
        conversationId,
        receiverId,
        content: 'Voice message',
        messageType: 'audio',
        attachmentUrl: audioUrl,
        attachmentType: 'audio/mp4',
        audioDuration: duration,
      });
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } catch (error) {
      console.error('Send voice note error:', error);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'failed' } : m
      ));
    }
  };

  // ── Selection handlers ────────────────────────────────────────────────────

  const enterSelectionMode = (messageId) => {
    setIsSelectionMode(true);
    setSelectedMessageIds(new Set([messageId]));
  };

  const toggleSelection = (messageId) => {
    const newSet = new Set(selectedMessageIds);
    if (newSet.has(messageId)) {
      newSet.delete(messageId);
      if (newSet.size === 0) setIsSelectionMode(false);
    } else {
      newSet.add(messageId);
    }
    setSelectedMessageIds(newSet);
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedMessageIds(new Set());
  };

  const getSelectedMessages = () => {
    return messages.filter(m => selectedMessageIds.has(m.id));
  };

  const canReply = selectedMessageIds.size === 1 && !getSelectedMessages()[0]?.isDeleted;
  const canPin = selectedMessageIds.size === 1;
  const canDeleteForEveryone = getSelectedMessages().every(m =>
    m.senderId === currentUserId && !m.isDeleted
  );

  // ── Pin selected ──────────────────────────────────────────────────────────

  const handlePinSelected = async () => {
    if (selectedMessageIds.size !== 1) return;
    const msgId = Array.from(selectedMessageIds)[0];
    const isPinned = pinnedMessageIds.includes(msgId);
    try {
      if (isPinned) {
        await messageActionsService.unpinMessage(conversationId, msgId);
      } else {
        await messageActionsService.pinMessage(conversationId, msgId);
      }
    } catch (error) {
      console.error('Pin error:', error);
    } finally {
      // Always exit, matching Flutter's try/finally — a failed pin/unpin
      // shouldn't leave the selection bar stuck open.
      exitSelectionMode();
    }
  };

  // ── Delete selected ───────────────────────────────────────────────────────

  const handleDeleteSelected = async () => {
    const selected = getSelectedMessages();
    if (selected.length === 0) return;

    const canDeleteForAll = selected.every(m => m.senderId === currentUserId);

    // Show delete options
    const options = ['Delete for me'];
    if (canDeleteForAll) options.unshift('Delete for everyone');
    options.push('Cancel');

    // Simple menu - in a real app, use a proper bottom sheet
    const choice = prompt(
      `Delete ${selected.length} message(s)?\nOptions: ${options.join(', ')}`
    );
    if (!choice || choice === 'Cancel') return;

    const forEveryone = choice === 'Delete for everyone';

    exitSelectionMode();

    for (const msg of selected) {
      try {
        if (forEveryone) {
          await messageActionsService.deleteForEveryone(conversationId, msg.id);
        } else {
          await messageActionsService.deleteForMe(conversationId, msg.id);
        }
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  // ── Forward selected ──────────────────────────────────────────────────────

  const handleForwardSelected = () => {
    const selected = getSelectedMessages();
    if (selected.length === 0) return;
    exitSelectionMode();
    navigate('/forward-message', { state: { messages: selected } });
  };

  // ── Start reply ───────────────────────────────────────────────────────────

  const startReply = (msg) => {
    setReplyingTo(msg);
    setIsSelectionMode(false);
    setSelectedMessageIds(new Set());
    inputRef.current?.focus();
  };

  // ── Filter blocked messages ──────────────────────────────────────────────

  const filterBlocked = (msgs) => {
    if (!isBlocked || !blockTimestamp) return msgs;
    return msgs.filter(m => {
      if (m.senderId === currentUserId) return true;
      return new Date(m.timestamp) < new Date(blockTimestamp);
    });
  };

  // ── Should show date divider ─────────────────────────────────────────────

  const shouldShowDateDivider = (current, previous) => {
    if (!previous) return true;
    const c = new Date(current.timestamp);
    const p = new Date(previous.timestamp);
    return c.getDate() !== p.getDate() ||
      c.getMonth() !== p.getMonth() ||
      c.getFullYear() !== p.getFullYear();
  };

  // ── Render date divider ──────────────────────────────────────────────────

  const renderDateDivider = (date) => {
    const label = formatDividerDate(date);
    return (
      <div className="flex items-center gap-2.5 py-3">
        <div className="flex-1 h-px bg-white/8" />
        <div className="px-2.5 py-1 rounded-xl" style={{ backgroundColor: 'rgba(35,38,44,0.6)' }}>
          <span className="text-white/50 text-xs font-medium">{label}</span>
        </div>
        <div className="flex-1 h-px bg-white/8" />
      </div>
    );
  };

  // ── Render message item ──────────────────────────────────────────────────

  const renderMessageItem = (msg, index, filtered) => {
    const isMine = msg.senderId === currentUserId;
    const prev = index > 0 ? filtered[index - 1] : null;
    const showTail = !prev || (prev.senderId === currentUserId) !== isMine;
    const effectiveStatus = isMine ? (liveStatuses[msg.id] || msg.status) : null;
    const showDivider = shouldShowDateDivider(msg, prev);
    const isHighlighted = highlightedMessageId === msg.id;

    // Store message key
    if (!messageKeys.current.has(msg.id)) {
      messageKeys.current.set(msg.id, React.createRef());
    }

    const senderName = isMine ? null : (receiverProfile?.displayName || title);

    return (
      <div key={msg.id} ref={messageKeys.current.get(msg.id)}>
        {showDivider && renderDateDivider(msg.timestamp)}
        <div
          className={`transition-colors duration-400 ${
            isHighlighted ? 'bg-filatom-primary/20' : 'bg-transparent'
          }`}
        >
          <MessageBubble
            content={msg.isDeleted ? 'This message was deleted' : msg.content}
            time={formatTime(msg.timestamp)}
            isMine={isMine}
            showTail={showTail}
            status={effectiveStatus}
            isDeleted={msg.isDeleted}
            messageType={msg.messageType}
            attachmentUrl={msg.attachmentUrl}
            localFilePath={msg.localFilePath}
            audioDuration={msg.audioDuration}
            isForwarded={msg.isForwarded || false}
            replyToId={msg.replyToMessageId}
            replyToContent={msg.replyToContent}
            replyToSenderId={msg.replyToSenderId}
            replyToType={msg.replyToType}
            replyToAttachmentUrl={msg.replyToAttachmentUrl}
            isSelected={selectedMessageIds.has(msg.id)}
            isSelectionMode={isSelectionMode}
            currentUserId={currentUserId}
            senderName={senderName}
            onLongPress={() => enterSelectionMode(msg.id)}
            onTap={isSelectionMode ? () => toggleSelection(msg.id) : undefined}
            onSwipeReply={msg.isDeleted ? undefined : () => startReply(msg)}
            onScrollToReply={scrollToMessage}
          />
        </div>
      </div>
    );
  };

  // ── Render blocked notice ────────────────────────────────────────────────

  const renderBlockNotice = () => {
    return (
      <div className="px-6 py-4 flex justify-center">
        <div className="px-4 py-2.5 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <p className="text-white/50 text-xs text-center">
            You blocked this contact. New messages are hidden.
          </p>
        </div>
      </div>
    );
  };

  // ── Render block banner ──────────────────────────────────────────────────

  const renderBlockBanner = () => {
    return (
      <div
        className="w-full px-5 py-3.5 flex flex-col items-center gap-3"
        style={{
          backgroundColor: 'rgba(8,9,11,0.88)',
          backdropFilter: 'blur(20px)',
          borderTop: '0.5px solid rgba(255,255,255,0.07)',
        }}
      >
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-red-500/75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <span className="text-white/50 text-xs font-medium">You blocked {title}</span>
        </div>
        <button
          onClick={unblockUser}
          className="w-full h-11 rounded-xl text-red-500 font-semibold text-sm border border-red-500/35"
          style={{ backgroundColor: 'rgba(255,23,68,0.12)' }}
        >
          Unblock
        </button>
      </div>
    );
  };

  // ── Render can't message banner ─────────────────────────────────────────

  const renderCantMessageBanner = () => {
    return (
      <div
        className="w-full px-6 py-4 flex items-center justify-center gap-2"
        style={{
          backgroundColor: 'rgba(8,9,11,0.88)',
          backdropFilter: 'blur(20px)',
          borderTop: '0.5px solid rgba(255,255,255,0.07)',
        }}
      >
        <svg className="w-4 h-4 text-white/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-white/40 text-xs font-medium text-center">
          You can't send messages to this contact.
        </span>
      </div>
    );
  };

  // ── Bottom bar (mirrors Flutter's _buildBottomBar: mutually exclusive) ────

  const renderBottomBar = () => {
    if (!blockStateLoaded) return null;
    if (isBlocked) return renderBlockBanner();
    if (amIBlocked) return renderCantMessageBanner();

    return (
      <MessageInput
        controller={controller}
        focusNode={inputRef}
        isSending={isSending}
        onSend={handleSend}
        onImageSelected={(image) => {
          setPendingImages((prev) => [...prev, image]);
          inputRef.current?.focus();
        }}
        onVoiceRecorded={handleVoiceRecorded}
        onEmojiToggle={() => setShowEmojiPicker(!showEmojiPicker)}
        showEmojiPicker={showEmojiPicker}
        pendingImages={pendingImages}
        onRemovePendingMedia={() => setPendingImages([])}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
        currentUserId={currentUserId}
      />
    );
  };

  // ── Create a message input controller ─────────────────────────────────────

  // Bridges MessageInput's controller-style API onto the plain `inputRef`
  // used elsewhere in this screen. `setElement` is the critical piece: it's
  // how the actual rendered <textarea> DOM node ends up assigned to
  // inputRef.current — without it, inputRef.current stays null forever and
  // handleSend() always reads an empty string.
  const createController = (ref) => {
    return {
      get text() {
        return ref.current?.value || '';
      },
      set text(value) {
        if (ref.current) {
          ref.current.value = value;
        }
      },
      setValue(value) {
        if (ref.current) {
          ref.current.value = value;
        }
      },
      setElement(el) {
        ref.current = el;
      },
      clear() {
        if (ref.current) {
          ref.current.value = '';
        }
      },
      focus() {
        ref.current?.focus();
      },
      addEventListener: (event, handler) => {
        ref.current?.addEventListener(event, handler);
      },
      removeEventListener: (event, handler) => {
        ref.current?.removeEventListener(event, handler);
      },
    };
  };

  const controller = createController(inputRef);

  // ── Main render ───────────────────────────────────────────────────────────

  const filtered = filterBlocked(messages);

  const body = (
    <div className={`${embedded ? 'h-full' : 'h-screen'} bg-filatom-bg relative flex flex-col overflow-hidden`}>
      {/* Wallpaper */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${chatWallpaper})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 z-0 bg-black/45" />

      {/* Top scrim for legibility against the wallpaper, mirrors Flutter's gradient */}
      <div
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
        style={{
          height: 90,
          background: `linear-gradient(to bottom, rgba(8,9,11,0.95) 0%, rgba(8,9,11,0.75) 60%, rgba(8,9,11,0) 100%)`,
        }}
      />

      {/* App Bar - Normal or Selection Mode */}
      <div className="relative z-20 flex-shrink-0">
        {isSelectionMode ? (
          <MessageSelectionBar
            selectedCount={selectedMessageIds.size}
            canReply={canReply}
            canPin={canPin}
            canDeleteForEveryone={canDeleteForEveryone}
            onClose={exitSelectionMode}
            onReply={canReply ? () => startReply(getSelectedMessages()[0]) : null}
            onPin={canPin ? handlePinSelected : null}
            onDelete={handleDeleteSelected}
            onForward={handleForwardSelected}
          />
        ) : (
          <div className="px-3 pt-3 pb-1.5">
            <div className="flex items-center gap-2">
              {/* Back button — hidden when embedded in the desktop split
                  view, since the conversation list is already visible
                  alongside and there's nowhere meaningful to navigate
                  back to. Standalone (routed) usage keeps it. */}
              {!embedded && (
                <button
                  onClick={() => navigate(-1)}
                  className="w-9.5 h-9.5 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: 'rgba(35,38,44,0.3)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid #23262C',
                  }}
                >
                  <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              {/* User info */}
              <button
                onClick={() => navigate(`/profile/${receiverId}`)}
                className="flex-1 flex items-center gap-2.5 px-1.5 py-1 rounded-full"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid #23262C',
                }}
              >
                <UserAvatar
                  size={40}
                  imageUrl={receiverProfile?.hasProfileImage ? receiverProfile.profileImage : avatarUrl}
                  name={receiverProfile?.displayName || title}
                />
                <div className="flex-1 text-left min-w-0">
                  <p className="text-white text-sm font-semibold truncate">
                    {receiverProfile?.displayName || title}
                  </p>
                  {!isBlocked && !amIBlocked && (
                    <p className={`text-xs ${presence.isOnline ? 'text-green-500 font-medium' : 'text-white/50'}`}>
                      {getPresenceText()}
                    </p>
                  )}
                  {isBlocked && (
                    <p className="text-red-500 text-[10px] font-semibold">Blocked</p>
                  )}
                </div>
              </button>

              {/* More button — on desktop, when this chat is embedded in
                  ConversationsScreen's split view, opens a small menu with
                  "Close chat" to return the right pane to its default
                  "Select a conversation" state. */}
              <div className="relative">
                <button
                  onClick={embedded ? () => setShowMoreMenu((v) => !v) : undefined}
                  className="w-9.5 h-9.5 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: 'rgba(35,38,44,0.3)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid #23262C',
                  }}
                >
                  <svg className="w-5.5 h-5.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </button>

                {embedded && showMoreMenu && (
                  <>
                    <button
                      className="fixed inset-0 z-10 cursor-default"
                      onClick={() => setShowMoreMenu(false)}
                      aria-label="Close menu"
                    />
                    <div
                      className="absolute right-0 top-full mt-2 w-48 rounded-xl overflow-hidden shadow-xl z-20 border border-white/10"
                      style={{ backgroundColor: COLORS.cardLight }}
                    >
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          onClose?.();
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-left text-white text-sm font-medium hover:bg-white/5"
                      >
                        <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Close chat
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pinned Messages Banner */}
      {pinnedMessageInfos.length > 0 && (
        <div className="relative z-10 flex-shrink-0">
          <PinnedMessagesBanner
            pinnedMessages={pinnedMessageInfos}
            onTap={scrollToMessage}
            onUnpin={unpinMessage}
          />
        </div>
      )}

      {/* Messages — flex-1 fills remaining space, mirrors Flutter's Expanded(...) */}
      <div
        ref={scrollRef}
        className="relative z-10 flex-1 overflow-y-auto px-2.5 py-3 min-h-0"
      >
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-white/30 text-center text-sm leading-relaxed">
              {isBlocked
                ? 'Messages from this contact\nare hidden while blocked.'
                : 'No messages yet.\nSay hello! 👋'}
            </p>
          </div>
        ) : (
          filtered.map((msg, index) => renderMessageItem(msg, index, filtered))
        )}
        {isBlocked && renderBlockNotice()}
      </div>

      {/* Input area — mutually exclusive with the block/can't-message banners */}
      <div className="relative z-10 flex-shrink-0">
        {renderBottomBar()}
      </div>
    </div>
  );

  // Embedded usage (desktop split view) fills whatever space its parent
  // pane gives it — no letterboxing needed, that pane already has a
  // sensible width from ConversationsScreen's layout.
  if (embedded) {
    return body;
  }

  // Standalone usage (routed to directly from another page): letterbox on
  // desktop so the conversation reads as an intentional column instead of
  // stretching bubbles across an ultra-wide browser. No-op on mobile since
  // real phone widths are always under STANDALONE_MAX_WIDTH.
  return (
    <div className="h-screen bg-black flex items-stretch justify-center overflow-hidden">
      <div className="w-full h-full md:shadow-2xl md:shadow-black/60" style={{ maxWidth: `${STANDALONE_MAX_WIDTH}px` }}>
        {body}
      </div>
    </div>
  );
};

export default ChatScreen;